import * as React from 'react';
import {DirectUpload} from './DirectUpload';
import {Controller} from 'react-hook-form';
import {MediaPreviewComponent} from './MediaPreview';
import {useMediaComponents} from './MediaComponents.use-case';
import {MediaComponentProps} from './MediaComponents.type';

export const MediaComponent = ({
  handleMediaUpdate,
  imageUrl,
  videoUrl,
  control,
  onUploadStatusChange,
  ...props
}: MediaComponentProps): JSX.Element => {
  const {updateUploadStatus, isAnyUploading} = useMediaComponents();

  const onUploadStatusChangeRef = React.useRef(onUploadStatusChange);
  const prevIsAnyUploadingRef = React.useRef<boolean | null>(null);

  React.useEffect(() => {
    onUploadStatusChangeRef.current = onUploadStatusChange;
  }, [onUploadStatusChange]);

  const handleImageUploadStatusChange = React.useCallback(
    (isUploading: boolean) => {
      updateUploadStatus('image', isUploading);
    },
    [updateUploadStatus],
  );

  const handleVideoUploadStatusChange = React.useCallback(
    (isUploading: boolean) => {
      updateUploadStatus('video', isUploading);
    },
    [updateUploadStatus],
  );

  React.useEffect(() => {
    if (prevIsAnyUploadingRef.current !== isAnyUploading) {
      prevIsAnyUploadingRef.current = isAnyUploading;
      onUploadStatusChangeRef.current?.(isAnyUploading);
    }
  }, [isAnyUploading]);

  const imageFile = React.useMemo(() => {
    if (imageUrl && !imageUrl.includes('missing')) {
      return {preview: imageUrl};
    }
    return undefined;
  }, [imageUrl]);

  const videoFile = React.useMemo(() => {
    if (videoUrl) {
      return {preview: videoUrl};
    }
    return undefined;
  }, [videoUrl]);

  const removeAllHandler = React.useCallback(() => {}, []);

  return (
    <div className="row">
      <div className="col s6">
        <Controller
          control={control}
          name="coverUrl"
          render={({field}) => (
            <DirectUpload
              mediaType="image"
              folder="images"
              onUploadComplete={(url) => {
                field.onChange(url);
                field.onBlur();
              }}
              onUploadStatusChange={handleImageUploadStatusChange}>
              <MediaPreviewComponent
                mediaType="image"
                file={imageFile}
                removeAll={removeAllHandler}
                isUploading={false}
                uploadProgress={0}
              />
            </DirectUpload>
          )}
        />
      </div>
      {videoUrl !== undefined && (
        <div className="col s6">
          <Controller
            control={control}
            name="videoUrl"
            render={({field}) => (
              <DirectUpload
                mediaType="video"
                folder="videos"
                onUploadComplete={(url) => {
                  field.onChange(url);
                  field.onBlur();
                }}
                onUploadStatusChange={handleVideoUploadStatusChange}>
                <MediaPreviewComponent
                  mediaType="video"
                  file={videoFile}
                  removeAll={removeAllHandler}
                  isUploading={false}
                  uploadProgress={0}
                />
              </DirectUpload>
            )}
          />
        </div>
      )}
    </div>
  );
};
